import {
  GizmoHelper,
  GizmoViewport,
  OrbitControls,
  TransformControls,
} from '@react-three/drei';
import { Canvas, useFrame } from '@react-three/fiber';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';

import { BVHLoader } from '@cartwhl/3d/bvh';

import { useStore } from '../motionEditorStore';
import {
  type Constraint,
  END_EFFECTOR_COLORS,
  END_EFFECTOR_NAMES,
  type EndEffectorName,
  findEndEffectorBone,
} from '../types';
import TimelineBar from './TimelineBar';

// ─── End Effector Trajectory Curves ───────────────────────────

/**
 * Pre-compute world positions for each end effector at every frame.
 * Uses a separate BVH parse so it doesn't interfere with the live skeleton.
 */
function computeEndEffectorTrajectories(
  bvhText: string,
  frameRate: number,
): Map<EndEffectorName, Float32Array> {
  const loader = new BVHLoader();
  const result = loader.parse(bvhText);
  const bones = result.skeleton.bones;
  const jointNames = bones.map((b: THREE.Bone) => b.name);

  const eeIndices = new Map<EndEffectorName, number>();
  for (const eeName of END_EFFECTOR_NAMES) {
    const idx = findEndEffectorBone(jointNames, eeName);
    if (idx >= 0) eeIndices.set(eeName, idx);
  }

  // Temporary scene graph for sampling
  const tempRoot = new THREE.Group();
  const rootBone = bones[0];
  if (rootBone) tempRoot.add(rootBone);

  const mixer = new THREE.AnimationMixer(tempRoot);
  const action = mixer.clipAction(result.clip);
  action.setLoop(THREE.LoopOnce, 1);
  action.clampWhenFinished = true;
  action.play();

  const duration = result.clip.duration;
  const totalFrames = Math.max(1, Math.floor(duration * frameRate));

  const trajectories = new Map<EndEffectorName, Float32Array>();
  for (const eeName of eeIndices.keys()) {
    trajectories.set(eeName, new Float32Array(totalFrames * 3));
  }

  const worldPos = new THREE.Vector3();

  for (let f = 0; f < totalFrames; f++) {
    mixer.setTime(f / frameRate);
    tempRoot.updateMatrixWorld(true);

    for (const [eeName, boneIdx] of eeIndices) {
      bones[boneIdx].getWorldPosition(worldPos);
      const arr = trajectories.get(eeName)!;
      arr[f * 3] = worldPos.x;
      arr[f * 3 + 1] = worldPos.y;
      arr[f * 3 + 2] = worldPos.z;
    }
  }

  mixer.stopAllAction();
  return trajectories;
}

function EndEffectorTrajectories({
  bvhText,
  frameRate,
}: {
  bvhText: string;
  frameRate: number;
}) {
  const trajectories = useMemo(
    () => computeEndEffectorTrajectories(bvhText, frameRate),
    [bvhText, frameRate],
  );

  const lineObjects = useMemo(() => {
    const result: { name: EndEffectorName; line: THREE.Line }[] = [];
    for (const [eeName, positions] of trajectories) {
      // Build THREE.Vector3 array from the flat Float32Array
      const numPts = positions.length / 3;
      const points: THREE.Vector3[] = [];
      for (let i = 0; i < numPts; i++) {
        points.push(
          new THREE.Vector3(
            positions[i * 3],
            positions[i * 3 + 1],
            positions[i * 3 + 2],
          ),
        );
      }

      // Smooth with CatmullRomCurve3 (same approach as server.py webapp)
      const curve = new THREE.CatmullRomCurve3(points);
      const smoothPoints = curve.getPoints(numPts * 3);

      const geometry = new THREE.BufferGeometry().setFromPoints(smoothPoints);
      const material = new THREE.LineBasicMaterial({
        color: END_EFFECTOR_COLORS[eeName],
        transparent: true,
        opacity: 0.45,
        depthWrite: false,
      });
      result.push({ name: eeName, line: new THREE.Line(geometry, material) });
    }
    return result;
  }, [trajectories]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      for (const l of lineObjects) {
        l.line.geometry.dispose();
        (l.line.material as THREE.Material).dispose();
      }
    };
  }, [lineObjects]);

  return (
    <>
      {lineObjects.map(({ name, line }) => (
        <primitive key={name} object={line} />
      ))}
    </>
  );
}

// Bones matching these patterns are extremities (fingers, toes) — skip rendering
const EXTREMITY_PATTERN = /\b(thumb|index|middle|ring|pinky|toe)\d*/i;
function isExtremityBone(name: string): boolean {
  return EXTREMITY_PATTERN.test(name);
}

// ─── Skeleton Scene ───────────────────────────────────────────

interface BVHData {
  skeleton: THREE.Skeleton;
  clip: THREE.AnimationClip;
  jointNames: string[];
  endEffectorBoneIndices: Map<EndEffectorName, number>;
  /** Indices of bones to hide (fingers, toes, leaf end-sites) */
  skipBoneIndices: Set<number>;
}

function parseBVH(bvhText: string): BVHData {
  const loader = new BVHLoader();
  const result = loader.parse(bvhText);

  const bones = result.skeleton.bones;
  const jointNames = bones.map((b: THREE.Bone) => b.name);
  const endEffectorBoneIndices = new Map<EndEffectorName, number>();
  for (const eeName of END_EFFECTOR_NAMES) {
    const idx = findEndEffectorBone(jointNames, eeName);
    if (idx >= 0) {
      endEffectorBoneIndices.set(eeName, idx);
    }
  }

  const eeIndexSet = new Set(endEffectorBoneIndices.values());

  // Build set of bone indices to skip rendering:
  // 1. Bones whose names match extremity patterns (fingers, toes)
  // 2. Leaf bones (no Bone children) that are NOT end effectors and NOT
  //    children of end effectors (e.g. foot is child of ankle)
  const skipBoneIndices = new Set<number>();
  for (let i = 0; i < bones.length; i++) {
    if (isExtremityBone(bones[i].name)) {
      skipBoneIndices.add(i);
      continue;
    }
    if (eeIndexSet.has(i)) continue;
    // Keep bones whose parent is an end effector (e.g. left_foot under left_ankle)
    const parent = bones[i].parent;
    if (parent instanceof THREE.Bone) {
      const parentIdx = bones.indexOf(parent);
      if (eeIndexSet.has(parentIdx)) continue;
    }
    const hasBoneChild = bones[i].children.some(
      (c: THREE.Object3D) => c instanceof THREE.Bone,
    );
    if (!hasBoneChild) {
      skipBoneIndices.add(i);
    }
  }

  return {
    skeleton: result.skeleton,
    clip: result.clip,
    jointNames,
    endEffectorBoneIndices,
    skipBoneIndices,
  };
}

// ─── Joint Sphere ─────────────────────────────────────────────

function JointSphere({
  bone,
  radius,
  color,
  isEndEffector,
  eeName,
  meshRef,
}: {
  bone: THREE.Bone;
  radius: number;
  color: string;
  isEndEffector: boolean;
  eeName?: EndEffectorName;
  meshRef?: React.RefObject<THREE.Mesh | null>;
}) {
  const internalRef = useRef<THREE.Mesh>(null);
  const ref = meshRef || internalRef;
  const worldPos = useMemo(() => new THREE.Vector3(), []);

  useFrame(() => {
    if (ref.current && bone) {
      bone.getWorldPosition(worldPos);
      ref.current.position.copy(worldPos);
    }
  });

  return (
    <mesh ref={ref} userData={{ isEndEffector, eeName }}>
      <sphereGeometry
        args={[radius, isEndEffector ? 16 : 8, isEndEffector ? 16 : 8]}
      />
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={isEndEffector ? 0.3 : 0}
        roughness={0.4}
      />
    </mesh>
  );
}

// ─── Bone Cylinder ────────────────────────────────────────────

function BoneCylinder({
  parentBone,
  childBone,
}: {
  parentBone: THREE.Bone;
  childBone: THREE.Bone;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const parentPos = useMemo(() => new THREE.Vector3(), []);
  const childPos = useMemo(() => new THREE.Vector3(), []);
  const midpoint = useMemo(() => new THREE.Vector3(), []);
  const direction = useMemo(() => new THREE.Vector3(), []);

  useFrame(() => {
    if (!meshRef.current) return;
    parentBone.getWorldPosition(parentPos);
    childBone.getWorldPosition(childPos);

    const dist = parentPos.distanceTo(childPos);
    if (dist < 0.001) {
      meshRef.current.visible = false;
      return;
    }

    meshRef.current.visible = true;
    midpoint.lerpVectors(parentPos, childPos, 0.5);
    meshRef.current.position.copy(midpoint);

    direction.subVectors(childPos, parentPos).normalize();
    const quaternion = new THREE.Quaternion();
    quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
    meshRef.current.quaternion.copy(quaternion);

    meshRef.current.scale.set(1, dist, 1);
  });

  return (
    <mesh ref={meshRef}>
      <cylinderGeometry args={[0.008, 0.008, 1, 6]} />
      <meshStandardMaterial color="#8899aa" roughness={0.6} />
    </mesh>
  );
}

// ─── Constraint Markers (always visible) ──────────────────────

function ConstraintMarker({
  position,
  constraint,
  color,
  isCurrentFrame,
}: {
  position: THREE.Vector3;
  constraint: Constraint;
  color: string;
  isCurrentFrame: boolean;
}) {
  // Octahedron rotated 45° on Z to look like a diamond
  const rotation = useMemo(() => new THREE.Euler(0, 0, Math.PI / 4), []);

  const pos = useMemo(
    () =>
      new THREE.Vector3(
        position.x + constraint.dx,
        position.y + constraint.dy,
        position.z + constraint.dz,
      ),
    [position, constraint.dx, constraint.dy, constraint.dz],
  );

  return (
    <mesh position={pos} rotation={rotation}>
      <octahedronGeometry args={[isCurrentFrame ? 0.025 : 0.018, 0]} />
      <meshStandardMaterial
        color={color}
        transparent
        opacity={isCurrentFrame ? 0.7 : 0.3}
        roughness={0.5}
        depthWrite={false}
      />
    </mesh>
  );
}

function ConstraintMarkers({
  bvhData,
  sourceBvhData,
}: {
  bvhData: BVHData;
  sourceBvhData: BVHData | null;
}) {
  const store = useStore();
  const snap = store.state;

  // Use source skeleton for stable target positions; fall back to current if no source yet
  const refData = sourceBvhData ?? bvhData;

  // Pre-compute world positions for all end effectors at every frame
  const eePositions = useMemo(() => {
    const bones = refData.skeleton.bones;
    const tempRoot = new THREE.Group();
    const rootBone = bones[0];
    if (rootBone) tempRoot.add(rootBone);

    const mixer = new THREE.AnimationMixer(tempRoot);
    const action = mixer.clipAction(refData.clip);
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = true;
    action.play();

    const duration = refData.clip.duration;
    const fps = snap.frameRate || 30;
    const totalFrames = Math.max(1, Math.floor(duration * fps));

    const positions = new Map<string, THREE.Vector3[]>();
    const worldPos = new THREE.Vector3();

    for (let f = 0; f < totalFrames; f++) {
      mixer.setTime(f / fps);
      tempRoot.updateMatrixWorld(true);

      for (const [eeName, boneIdx] of refData.endEffectorBoneIndices) {
        bones[boneIdx].getWorldPosition(worldPos);
        const key = eeName as string;
        if (!positions.has(key)) positions.set(key, []);
        positions.get(key)!.push(worldPos.clone());
      }
    }

    mixer.stopAllAction();
    return positions;
  }, [refData, snap.frameRate]);

  const allConstraints = snap.constraints as Constraint[];
  if (allConstraints.length === 0) return null;

  return (
    <>
      {allConstraints.map((c) => {
        const eeName = c.endEffector as EndEffectorName;
        const color = END_EFFECTOR_COLORS[eeName] ?? '#ffffff';
        const framePositions = eePositions.get(eeName);
        if (!framePositions || c.frame >= framePositions.length) return null;
        const bonePos = framePositions[c.frame];
        return (
          <ConstraintMarker
            key={`${c.frame}-${c.endEffector}`}
            position={bonePos}
            constraint={c}
            color={color}
            isCurrentFrame={c.frame === snap.currentFrame}
          />
        );
      })}
    </>
  );
}

// ─── End Effector Dragger ─────────────────────────────────────

function EndEffectorDragger({
  bvhData,
  sourceBvhData,
}: {
  bvhData: BVHData;
  sourceBvhData: BVHData | null;
}) {
  const store = useStore();
  const snap = store.state;
  const selectedEffector = snap.selectedEffector;

  // Use state as callback-ref so we re-render once the Three.js objects exist
  const [handleObj, setHandleObj] = useState<THREE.Mesh | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [transformCtrl, setTransformCtrl] = useState<any>(null);

  const originalPos = useRef(new THREE.Vector3());
  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  // Track cumulative displacement across multiple drags on the same frame+effector
  const cumulativeDisp = useRef(new THREE.Vector3());
  const worldPos = useMemo(() => new THREE.Vector3(), []);

  // Reset position when the handle mesh is created, effector changes, or frame changes
  useEffect(() => {
    if (!handleObj || !selectedEffector) return;
    const boneIdx = bvhData.endEffectorBoneIndices.get(selectedEffector);
    if (boneIdx === undefined) return;
    const bone = bvhData.skeleton.bones[boneIdx];
    if (!bone) return;
    bone.getWorldPosition(worldPos);
    handleObj.position.copy(worldPos);
    originalPos.current.copy(worldPos);
    hasDragged.current = false;
    cumulativeDisp.current.set(0, 0, 0);
  }, [handleObj, selectedEffector, snap.currentFrame, bvhData, worldPos]);

  // Attach "dragging-changed" listener once the TransformControls native object is available
  useEffect(() => {
    if (!transformCtrl) return;

    const onDraggingChanged = (event: { value: boolean }) => {
      isDragging.current = event.value;

      if (event.value) {
        // Drag started — snapshot position before this drag
        // (may already be offset from previous drags)
        if (handleObj) {
          originalPos.current.copy(handleObj.position);
        }
      } else {
        // Drag ended — compute incremental displacement and accumulate
        if (!handleObj || !store.state.selectedEffector) return;

        hasDragged.current = true;

        const newPos = handleObj.position;
        const incDx = newPos.x - originalPos.current.x;
        const incDy = newPos.y - originalPos.current.y;
        const incDz = newPos.z - originalPos.current.z;

        cumulativeDisp.current.x += incDx;
        cumulativeDisp.current.y += incDy;
        cumulativeDisp.current.z += incDz;

        // Compute total delta from the SOURCE skeleton (not the current/regenerated one)
        // totalDelta = (currentBonePos - sourceBonePos) + cumulativeDragDisplacement
        const refData = sourceBvhData ?? bvhData;
        const effector = store.state.selectedEffector!;
        const sBoneIdx = refData.endEffectorBoneIndices.get(effector);
        const cBoneIdx = bvhData.endEffectorBoneIndices.get(effector);

        if (sBoneIdx !== undefined && cBoneIdx !== undefined) {
          const sourceBone = refData.skeleton.bones[sBoneIdx];
          const currentBone = bvhData.skeleton.bones[cBoneIdx];
          const sp = new THREE.Vector3();
          const cp = new THREE.Vector3();
          sourceBone.getWorldPosition(sp);
          currentBone.getWorldPosition(cp);

          // skeletonShift = how much the current skeleton has already moved from source
          const totalDx = cp.x - sp.x + cumulativeDisp.current.x;
          const totalDy = cp.y - sp.y + cumulativeDisp.current.y;
          const totalDz = cp.z - sp.z + cumulativeDisp.current.z;

          if (
            Math.abs(totalDx) > 0.001 ||
            Math.abs(totalDy) > 0.001 ||
            Math.abs(totalDz) > 0.001
          ) {
            store.addConstraint({
              frame: store.state.currentFrame,
              endEffector: effector,
              dx: totalDx,
              dy: totalDy,
              dz: totalDz,
            });
          }
        }
      }
    };

    transformCtrl.addEventListener('dragging-changed', onDraggingChanged);
    return () => {
      transformCtrl.removeEventListener('dragging-changed', onDraggingChanged);
    };
  }, [transformCtrl, handleObj, bvhData, sourceBvhData, store]);

  // Keep the handle mesh at the bone position each frame
  // Skip if the user is dragging or has already dragged (keep it at the dragged position)
  useFrame(() => {
    if (
      !store.state.selectedEffector ||
      !handleObj ||
      isDragging.current ||
      hasDragged.current
    )
      return;

    const boneIdx = bvhData.endEffectorBoneIndices.get(
      store.state.selectedEffector,
    );
    if (boneIdx === undefined) return;

    const bone = bvhData.skeleton.bones[boneIdx];
    if (!bone) return;

    bone.getWorldPosition(worldPos);
    handleObj.position.copy(worldPos);
  });

  if (!selectedEffector) return null;

  const boneIdx = bvhData.endEffectorBoneIndices.get(selectedEffector);
  if (boneIdx === undefined) return null;

  return (
    <>
      {/* Visible marker sphere at the bone position — NOT a child of TransformControls */}
      <mesh ref={setHandleObj} key={selectedEffector}>
        <sphereGeometry args={[0.025]} />
        <meshBasicMaterial
          color={END_EFFECTOR_COLORS[selectedEffector]}
          transparent
          opacity={0.6}
        />
      </mesh>

      {/* TransformControls attaches to the mesh via the object prop */}
      {handleObj && (
        <TransformControls
          ref={setTransformCtrl}
          object={handleObj}
          mode="translate"
          size={0.6}
        />
      )}
    </>
  );
}

// ─── Skeleton Renderer ────────────────────────────────────────

function SkeletonScene({ bvhText }: { bvhText: string }) {
  const store = useStore();
  const snap = store.state;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const orbitRef = useRef<any>(null);

  // Parse BVH
  const bvhData = useMemo(() => parseBVH(bvhText), [bvhText]);

  // ─── Source (checkpoint) skeleton — hidden, used for stable constraint targets ───
  const sourceBvhText = store.getSourceBvhText();
  const sourceBvhData = useMemo(
    () =>
      sourceBvhText && sourceBvhText !== bvhText
        ? parseBVH(sourceBvhText)
        : null,
    [sourceBvhText, bvhText],
  );
  const sourceRootRef = useRef<THREE.Group>(null);
  const sourceMixerRef = useRef<THREE.AnimationMixer | null>(null);

  // Setup source skeleton + animation (hidden)
  useEffect(() => {
    if (!sourceRootRef.current || !sourceBvhData) return;
    while (sourceRootRef.current.children.length) {
      sourceRootRef.current.remove(sourceRootRef.current.children[0]);
    }
    const rootBone = sourceBvhData.skeleton.bones[0];
    if (rootBone) sourceRootRef.current.add(rootBone);
    const mixer = new THREE.AnimationMixer(sourceRootRef.current);
    const action = mixer.clipAction(sourceBvhData.clip);
    action.setLoop(THREE.LoopRepeat, Infinity);
    action.play();
    sourceMixerRef.current = mixer;
    mixer.setTime(0);
    return () => {
      mixer.stopAllAction();
    };
  }, [sourceBvhData]);

  // Root group to host the bone hierarchy
  const rootRef = useRef<THREE.Group>(null);
  const mixerRef = useRef<THREE.AnimationMixer | null>(null);

  // Setup skeleton + animation
  useEffect(() => {
    if (!rootRef.current) return;

    // Clear previous children
    while (rootRef.current.children.length) {
      rootRef.current.remove(rootRef.current.children[0]);
    }

    // Add skeleton root bone
    const rootBone = bvhData.skeleton.bones[0];
    if (rootBone) {
      rootRef.current.add(rootBone);
    }

    // Create mixer
    const mixer = new THREE.AnimationMixer(rootRef.current);
    const action = mixer.clipAction(bvhData.clip);
    action.setLoop(THREE.LoopRepeat, Infinity);
    action.play();
    mixerRef.current = mixer;

    // Set initial time
    mixer.setTime(0);

    return () => {
      mixer.stopAllAction();
    };
  }, [bvhData]);

  // Track whether playback is driving the mixer so the seek effect doesn't conflict
  const playbackDriven = useRef(false);

  // Seek to frame (only when user manually changes frame, e.g. slider)
  useEffect(() => {
    if (playbackDriven.current) return;
    if (mixerRef.current && bvhData) {
      const time = snap.currentFrame / snap.frameRate;
      mixerRef.current.setTime(time);
    }
    // Also seek the source skeleton
    if (sourceMixerRef.current) {
      const time = snap.currentFrame / snap.frameRate;
      sourceMixerRef.current.setTime(time);
    }
  }, [snap.currentFrame, snap.frameRate, bvhData, sourceBvhData]);

  // Playback — read directly from the mutable Valtio proxy, not the snapshot
  useFrame((_, delta) => {
    const s = store.state;
    if (!s.isPlaying || !mixerRef.current) {
      playbackDriven.current = false;
      return;
    }

    playbackDriven.current = true;

    // Advance mixer time with delta
    const scaledDelta = delta * s.playbackSpeed;
    mixerRef.current.update(scaledDelta);

    // Compute current frame from mixer time
    const currentTime = mixerRef.current.time;
    const totalDuration = bvhData.clip.duration;
    const wrappedTime = currentTime % totalDuration;
    const newFrame = Math.floor(wrappedTime * s.frameRate);

    if (newFrame !== s.currentFrame) {
      store.setFrame(newFrame);
    }

    // Keep source mixer in sync during playback
    if (sourceMixerRef.current) {
      sourceMixerRef.current.setTime(mixerRef.current.time);
    }
  });

  // Build bone parent-child pairs for cylinders
  const bonePairs = useMemo(() => {
    const pairs: { parent: THREE.Bone; child: THREE.Bone }[] = [];
    for (const bone of bvhData.skeleton.bones) {
      if (bone.parent && bone.parent instanceof THREE.Bone) {
        const ci = bvhData.skeleton.bones.indexOf(bone);
        const pi = bvhData.skeleton.bones.indexOf(bone.parent as THREE.Bone);
        // Skip if either end is a hidden bone
        if (bvhData.skipBoneIndices.has(ci) || bvhData.skipBoneIndices.has(pi))
          continue;
        pairs.push({ parent: bone.parent, child: bone });
      }
    }
    return pairs;
  }, [bvhData]);

  // End effector mesh refs for raycasting
  const eeMeshRefs = useRef<
    Map<EndEffectorName, React.RefObject<THREE.Mesh | null>>
  >(new Map());

  // Create refs for end effectors
  for (const eeName of END_EFFECTOR_NAMES) {
    if (!eeMeshRefs.current.has(eeName)) {
      eeMeshRefs.current.set(eeName, { current: null });
    }
  }

  // Click handling for end effectors
  const handlePointerDown = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      const mesh = e.object as THREE.Mesh;
      if (mesh.userData.isEndEffector && mesh.userData.eeName) {
        e.stopPropagation();
        store.selectEffector(mesh.userData.eeName);
      }
    },
    [store],
  );

  return (
    <>
      {/* Lights */}
      <ambientLight intensity={0.4} />
      <directionalLight position={[5, 10, 5]} intensity={0.8} castShadow />
      <directionalLight position={[-3, 5, -3]} intensity={0.3} />
      <hemisphereLight args={['#b1e1ff', '#b97a20', 0.3]} />

      {/* Floor — raycasting disabled so it doesn't intercept clicks on foot effectors */}
      <gridHelper args={[10, 20, '#555555', '#333333']} />
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, -0.001, 0]}
        receiveShadow
        ref={(mesh: THREE.Mesh | null) => {
          if (mesh) mesh.raycast = () => {};
        }}
      >
        <planeGeometry args={[10, 10]} />
        <meshStandardMaterial color="#1a1a2e" transparent opacity={0.5} />
      </mesh>

      {/* Camera controls */}
      <OrbitControls
        ref={orbitRef}
        makeDefault
        target={[0, 0.9, 0]}
        maxPolarAngle={Math.PI * 0.85}
      />

      {/* Gizmo */}
      <GizmoHelper alignment="bottom-right" margin={[60, 60]}>
        <GizmoViewport />
      </GizmoHelper>

      {/* Skeleton root group (hosts the bone hierarchy for AnimationMixer) */}
      <group ref={rootRef} visible={false} />

      {/* Hidden source skeleton for stable constraint references */}
      <group ref={sourceRootRef} visible={false} />

      {/* Visual joint spheres */}
      {bvhData.skeleton.bones.map((bone, i) => {
        // Skip finger/toe/leaf bones
        if (bvhData.skipBoneIndices.has(i)) return null;

        let isEE = false;
        let eeName: EndEffectorName | undefined;
        let color = '#cccccc';
        let radius = 0.015;

        for (const [name, boneIdx] of bvhData.endEffectorBoneIndices) {
          if (boneIdx === i) {
            isEE = true;
            eeName = name;
            color = END_EFFECTOR_COLORS[name];
            radius = 0.03;
            break;
          }
        }

        const selected = snap.selectedEffector === eeName;
        if (selected) radius = 0.04;

        return (
          <JointSphere
            key={bone.name}
            bone={bone}
            radius={radius}
            color={color}
            isEndEffector={isEE}
            eeName={eeName}
            meshRef={eeName ? eeMeshRefs.current.get(eeName) : undefined}
          />
        );
      })}

      {/* Click targets for all end effector spheres */}
      <group onPointerDown={handlePointerDown}>
        {Array.from(bvhData.endEffectorBoneIndices).map(([eeName, boneIdx]) => (
          <ClickableEffector
            key={eeName}
            bone={bvhData.skeleton.bones[boneIdx]}
            eeName={eeName}
          />
        ))}
      </group>

      {/* Bone cylinders */}
      {bonePairs.map(({ parent, child }) => (
        <BoneCylinder
          key={`${parent.name}-${child.name}`}
          parentBone={parent}
          childBone={child}
        />
      ))}

      {/* End effector trajectory curves */}
      {snap.showTrajectories && (
        <EndEffectorTrajectories bvhText={bvhText} frameRate={snap.frameRate} />
      )}

      {/* Constraint displacement markers (visible only on constrained frames) */}
      <ConstraintMarkers bvhData={bvhData} sourceBvhData={sourceBvhData} />

      {/* Transform gizmo for selected effector */}
      <EndEffectorDragger bvhData={bvhData} sourceBvhData={sourceBvhData} />
    </>
  );
}

// Invisible clickable sphere over end effectors
function ClickableEffector({
  bone,
  eeName,
}: {
  bone: THREE.Bone;
  eeName: EndEffectorName;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const worldPos = useMemo(() => new THREE.Vector3(), []);

  useFrame(() => {
    if (meshRef.current && bone) {
      bone.getWorldPosition(worldPos);
      meshRef.current.position.copy(worldPos);
    }
  });

  return (
    <mesh ref={meshRef} userData={{ isEndEffector: true, eeName }}>
      <sphereGeometry args={[0.05, 8, 8]} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
  );
}

// ─── Main Viewer Component ────────────────────────────────────

export default function MotionViewer() {
  const store = useStore();
  const snap = store.state;

  if (!snap.bvhText) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-100 text-gray-500">
        <p>Load a motion to view it here</p>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      <Canvas
        camera={{ position: [0, 1, 3], fov: 50, near: 0.01, far: 100 }}
        shadows
        gl={{ antialias: true }}
        onPointerMissed={() => store.selectEffector(null)}
      >
        <color attach="background" args={['#16213e']} />
        <SkeletonScene bvhText={snap.bvhText} />
      </Canvas>

      {/* Timeline overlay at bottom */}
      <TimelineBar />

      {/* Selected effector indicator */}
      {snap.selectedEffector && (
        <div className="pointer-events-none absolute top-3 right-3 rounded bg-black/60 px-2 py-1 text-xs text-white">
          Editing:{' '}
          <span
            className="font-bold"
            style={{ color: END_EFFECTOR_COLORS[snap.selectedEffector] }}
          >
            {snap.selectedEffector}
          </span>
          <span className="ml-2 text-gray-400">Click gizmo to drag</span>
        </div>
      )}
    </div>
  );
}
